import { Button } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { toTitleCase } from '@vrplatform/utils';
import { Box, Group, Modal, Tabs } from '@mantine/core';
import { Text } from '@mantine/core';
import { pluralize } from '@vrplatform/ui-common';
import { useCallback, useEffect, useRef, useState } from 'react';
import { type StoreApi, createStore, useStore } from 'zustand';
import {
  createTableSelectionSlice,
  useTableStoreReset,
} from '../../../../../stores';
import { TableStoreProvider } from '../../../../../stores';
import { ListingTable } from './ListingTable';
import { ReservationTable } from './ReservationTable';
import type { Entity, StoreProps } from './_types';
import { useEntityQuery } from './useExpenseListingReservationModalQuery';

type Props = {
  opened: boolean;
  closeModal: () => void;
  activeEntity: {
    id: string;
    type: Entity['type'];
  } | null;
  handleSubmit: (activeEntity: Entity) => void;
  type?: Entity['type'];
};

export const ExpenseListingReservationModal = (props: Props) => (
  <TableStoreProvider filterType="state">
    <SelectModal {...props} />
  </TableStoreProvider>
);

const SelectModal = ({
  opened,
  closeModal,
  activeEntity,
  handleSubmit,
  type: initialType,
}: Props) => {
  const { data: initialEntity } = useEntityQuery({
    activeEntity,
  });

  const [selectedType, setSelectedType] = useState<Entity['type']>('listing');

  const type = initialType || selectedType;

  const [store] = useState(
    createStore<StoreProps>((set, get, store) => ({
      ...createTableSelectionSlice(set, get, store),
      entity: null,
      setEntity: (entity) =>
        set((state) => ({
          ...state,
          entity,
        })),
      getEntity: () => get().entity,
      reset: () => set({ entity: null, selected: {} }),
    }))
  );

  const setEntity = useStore(store, (st) => st.setEntity);
  const resetStore = useTableStoreReset();
  const hasInitialized = useRef(false);
  const prevActiveEntityId = useRef<string | null>(null);
  const activeEntityId = activeEntity?.id ?? null;

  const handleTypeSelect = useCallback(
    (t: Entity['type']) => {
      resetStore();
      setEntity(null);
      setSelectedType(t);
    },
    [resetStore, setEntity]
  );

  useEffect(() => {
    if (!opened) {
      hasInitialized.current = false;
      prevActiveEntityId.current = activeEntityId;
      return;
    }

    if (prevActiveEntityId.current !== activeEntityId) {
      hasInitialized.current = false;
    }

    if (
      hasInitialized.current ||
      !activeEntityId ||
      !initialEntity ||
      initialEntity.id !== activeEntityId
    ) {
      prevActiveEntityId.current = activeEntityId;
      return;
    }

    const state = store.getState();
    state.setEntity({
      ...initialEntity,
      index: 0,
    });
    state.setSelected({ [initialEntity.id]: true });
    setSelectedType(initialEntity.type);

    hasInitialized.current = true;
    prevActiveEntityId.current = activeEntityId;
  }, [activeEntityId, initialEntity, opened, store]);

  return (
    <Modal
      centered
      withinPortal
      opened={opened}
      onClose={closeModal}
      title={'Select what to assign the expense to'}
      size={1300}
      zIndex={200}
      styles={{
        title: {
          fontWeight: 'bold',
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: 0,
          paddingTop: 2,
          minHeight: '80dvh',
        },
      }}
    >
      <Tabs
        value={type}
        keepMounted={false}
        onChange={(value) => {
          handleTypeSelect(value as Entity['type']);
        }}
        styles={(theme) => ({
          root: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          },
          panel: {
            flex: 1,
            marginTop: initialType ? undefined : theme.spacing.md,
            display: 'flex',
            flexDirection: 'column',
          },
        })}
      >
        {!initialType && (
          <Group>
            <TypeButton type="listing" activeType={type} />
            <TypeButton type="reservation" activeType={type} />
          </Group>
        )}

        <Tabs.Panel
          value={'reservation' satisfies Entity['type']}
          data-testid="select-reservation-table"
        >
          <ReservationTable store={store} />
        </Tabs.Panel>
        <Tabs.Panel
          value={'listing' satisfies Entity['type']}
          data-testid="select-listing-table"
        >
          <ListingTable store={store} />
        </Tabs.Panel>
      </Tabs>

      <Group
        pt="xs"
        pb="sm"
        justify="space-between"
        sx={(theme) => ({
          borderTop: `1px solid ${theme.colors.gray[2]}`,
        })}
      >
        <Selected store={store} type={type} />
        <Group mt="auto" mb="xs">
          <Button type="reset" onClick={closeModal}>
            Cancel
          </Button>
          <SubmitButton
            store={store}
            handleSubmit={handleSubmit}
            closeModal={closeModal}
            type={type}
          />
        </Group>
      </Group>
    </Modal>
  );
};

const TypeButton = ({
  type,
  activeType,
}: {
  type: Entity['type'];
  activeType: Entity['type'];
}) => {
  const isActive = type === activeType;

  return (
    <Tabs.Tab
      value={type}
      data-testid={`select-${type}-tab`}
      sx={(theme) => ({
        flex: 1,
        backgroundColor: isActive
          ? theme.colors[theme.primaryColor][0]
          : 'white',
        border: '1px solid',
        borderRadius: theme.radius.md,
        fontWeight: 'bold',
        borderColor: isActive
          ? theme.colors[theme.primaryColor][6]
          : theme.colors.gray[2],
        color: isActive ? theme.colors.neutral[7] : theme.colors.neutral[6],
        ':hover': {
          backgroundColor: isActive
            ? theme.colors[theme.primaryColor][0]
            : 'white',
          color: isActive ? theme.colors.neutral[7] : theme.colors.neutral[6],
        },
      })}
      leftSection={
        <Icon
          icon={type === 'reservation' ? 'CalendarEventIcon' : 'HomeIcon'}
          size={16}
          color={
            isActive
              ? (theme) => theme.colors[theme.primaryColor][7]
              : undefined
          }
        />
      }
    >
      {pluralize(2, toTitleCase(type))}
    </Tabs.Tab>
  );
};

const SubmitButton = ({
  store,
  handleSubmit,
  closeModal,
  type,
}: {
  store: StoreApi<StoreProps>;
  handleSubmit: Props['handleSubmit'];
  closeModal: () => void;
  type: Entity['type'];
}) => {
  const entity = useStore(store, (st) => st.entity);

  return (
    <Button
      type="submit"
      variant="primary"
      disabled={!entity}
      onClick={() => {
        if (entity) {
          closeModal();
          handleSubmit(entity);
        }
      }}
    >
      Select {type}
    </Button>
  );
};

const Selected = ({
  store,
  type,
}: {
  store: StoreApi<StoreProps>;
  type: Entity['type'];
}) => {
  const entity = useStore(store, (st) => st.entity);

  return (
    <Box>
      <Text size="xs" c="gray" mb={3}>
        Selected {type}:
      </Text>
      {!entity ? (
        <Text fw={500} size="lg">
          -
        </Text>
      ) : (
        <Group flex={1} gap="xs">
          <Icon icon={type === 'listing' ? 'HomeIcon' : 'UserIcon'} size={18} />
          <Box
            sx={{
              textAlign: 'left',
            }}
          >
            <Text fw={500} size="sm" lh="xs">
              {entity.title}
            </Text>
            <Text size="xs">{entity.description}</Text>
          </Box>
        </Group>
      )}
    </Box>
  );
};
